/**
 * Site Induction wizard config — data-driven step definitions.
 *
 * Phase 20 (Refactor 3, supplemented in Phase 20-02). Mirrors
 * `SITE_INDUCTION_WIZARD_STEPS` (../wizard-schemas/site-induction.ts).
 * 7 content steps + preview.
 *
 * Step renderer assignment:
 *   - `context`   → data-driven (3 fields)
 *   - `emergency` → data-driven (1 field — induction-specific
 *                   `reportingProcedures`; the assembly point / fire strategy /
 *                   nearest hospital / first aid facts are single-sourced from
 *                   the project row now, so the AI fireStrategy suggest is gone)
 *   - `ppe`       → data-driven (checklist — same 8 items as RAMS)
 *   - `hazards`   → custom (`SiteInductionHazardBuilder` — label-only
 *                   list; no severity unlike RAMS / CPP)
 *   - `signoff`   → data-driven (textarea)
 *   - `preview`   → custom (`SiteInductionPreview`)
 */
import type { WizardConfig } from "./types";

const PPE_OPTIONS = [
  { value: "hardHat", labelKey: "wizards.rams.ppeLabels.hardHat" },
  { value: "hiVis", labelKey: "wizards.rams.ppeLabels.hiVis" },
  { value: "safetyBoots", labelKey: "wizards.rams.ppeLabels.safetyBoots" },
  { value: "gloves", labelKey: "wizards.rams.ppeLabels.gloves" },
  { value: "eyeProtection", labelKey: "wizards.rams.ppeLabels.eyeProtection" },
  { value: "earProtection", labelKey: "wizards.rams.ppeLabels.earProtection" },
  { value: "respirator", labelKey: "wizards.rams.ppeLabels.respirator" },
  { value: "harness", labelKey: "wizards.rams.ppeLabels.harness" },
] as const;

export const SITE_INDUCTION_WIZARD_CONFIG: WizardConfig = {
  documentType: "SITE_INDUCTION",
  steps: [
    {
      id: "context",
      titleKey: "wizards.siteInduction.stepTitles.context",
      descriptionKey: "wizards.siteInduction.stepDescriptions.context",
      schemaRef: "SITE_INDUCTION.steps.context",
      fields: [
        {
          id: "projectTitle",
          type: "text",
          labelKey: "projects.fields.name",
          required: true,
        },
        {
          id: "siteAddress",
          type: "text",
          labelKey: "wizards.rams.siteAddressLabel",
          required: true,
        },
        {
          id: "scopeDescription",
          type: "textarea",
          labelKey: "projects.fields.scopeDescription",
          rows: 3,
        },
      ],
    },
    // Site rules are a single project-level fact edited once in project settings
    // (the former `rules` step was removed) — they render into the induction from
    // the project row.
    // Assembly point + fire strategy + nearest hospital + first aid
    // arrangements are single project-level facts edited once in project
    // settings — they render into the induction from the project row. Only the
    // induction-specific `reportingProcedures` is still collected here (and with
    // fireStrategy gone there is no AI section-suggest target left, so it's
    // dropped).
    {
      id: "emergency",
      titleKey: "wizards.siteInduction.stepTitles.emergency",
      descriptionKey: "wizards.siteInduction.stepDescriptions.emergency",
      schemaRef: "SITE_INDUCTION.steps.emergency",
      fields: [
        {
          id: "reportingProcedures",
          type: "textarea",
          labelKey: "wizards.siteInduction.fields.reportingProcedures",
          placeholderKey:
            "wizards.siteInduction.placeholders.reportingProcedures",
          required: true,
          rows: 3,
        },
      ],
    },
    {
      id: "ppe",
      titleKey: "wizards.siteInduction.stepTitles.ppe",
      descriptionKey: "wizards.siteInduction.stepDescriptions.ppe",
      schemaRef: "SITE_INDUCTION.steps.ppe",
      fields: [
        {
          id: "ppe",
          type: "checklist",
          labelKey: "wizards.siteInduction.stepTitles.ppe",
          required: true,
          options: PPE_OPTIONS,
        },
      ],
    },
    {
      id: "hazards",
      titleKey: "wizards.siteInduction.stepTitles.hazards",
      descriptionKey: "wizards.siteInduction.stepDescriptions.hazards",
      schemaRef: "SITE_INDUCTION.steps.hazards",
      customRenderer: "SiteInductionHazardBuilder",
    },
    // Welfare arrangements are a single project-level fact edited once in project
    // settings (the former `welfare` step was removed) — they render into the
    // induction from the project row.
    {
      id: "signoff",
      titleKey: "wizards.siteInduction.stepTitles.signoff",
      descriptionKey: "wizards.siteInduction.stepDescriptions.signoff",
      schemaRef: "SITE_INDUCTION.steps.signoff",
      fields: [
        {
          id: "signOffList",
          type: "textarea",
          labelKey: "wizards.rams.signOffLabel",
          helperKey: "wizards.rams.signOffHelper",
          required: true,
          rows: 4,
        },
      ],
    },
    {
      id: "preview",
      titleKey: "wizards.siteInduction.stepTitles.preview",
      descriptionKey: "wizards.siteInduction.stepDescriptions.preview",
      schemaRef: "SITE_INDUCTION.steps.preview",
      customRenderer: "SiteInductionPreview",
    },
  ],
};
